/**
 * SellerBalance — the seller's available wallet balance, shown under a seller
 * name on admin surfaces. Red + bold when the wallet is low (at/below the
 * seller's minimum, same rule that blocks order/appointment booking); muted grey
 * otherwise.
 *
 * Fed by the `seller_wallet` snapshot the admin APIs attach to seller-bearing
 * rows/details (utils/wallet.attach_seller_wallet): { balance, min_balance,
 * enforce, is_low }. Renders nothing when no snapshot is available.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { API } from "@/App";
import { Wallet } from "lucide-react";

const inr = (n) =>
  `₹${Number(n ?? 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

export function SellerBalance({ wallet, className = "" }) {
  if (!wallet || wallet.balance == null) return null;
  const low = !!wallet.is_low;
  return (
    <div
      className={`mt-0.5 flex items-center gap-1 text-xs tabular-nums ${
        low ? "font-semibold text-red-600" : "text-slate-400"
      } ${className}`}
      title={
        low
          ? `Wallet ${inr(wallet.balance)} is at/below the minimum ${inr(wallet.min_balance)} — recharge required`
          : `Wallet balance ${inr(wallet.balance)}`
      }
      data-testid="seller-balance"
    >
      <Wallet className="h-3 w-3 shrink-0" strokeWidth={low ? 2.5 : 2} />
      <span>{inr(wallet.balance)}</span>
      {low && (
        <span className="rounded bg-red-100 px-1 py-px text-[10px] font-semibold uppercase leading-none text-red-700">
          low
        </span>
      )}
    </div>
  );
}

/**
 * Shared one-time cache of the admin seller-wallet map (seller_id -> snapshot),
 * so <SellerBalanceById> works in ANY render scope — including module-level
 * DataTable column cells — without each page fetching or threading a hook value.
 * Fails soft: on error / non-admin (403) the map stays empty and nothing renders.
 */
let _walletMap = null;
let _walletPromise = null;
const _subscribers = new Set();

function _ensureWallets() {
  if (_walletMap || _walletPromise) return;
  _walletPromise = axios
    .get(`${API}/admin/seller-wallets`)
    .then((res) => {
      _walletMap = res.data || {};
    })
    .catch(() => {
      _walletMap = {};
    })
    .finally(() => {
      _subscribers.forEach((fn) => fn());
    });
}

/**
 * Seller balance looked up by seller_id from the shared admin wallet map — for
 * surfaces (esp. B2C) whose rows don't carry a `seller_wallet` snapshot. Renders
 * the same way as <SellerBalance>.
 */
export function SellerBalanceById({ sellerId, className = "" }) {
  const [, force] = useState(0);
  useEffect(() => {
    if (!sellerId) return undefined;
    const rerender = () => force((n) => n + 1);
    _subscribers.add(rerender);
    _ensureWallets();
    if (_walletMap) rerender();
    return () => _subscribers.delete(rerender);
  }, [sellerId]);
  if (!sellerId || !_walletMap) return null;
  return <SellerBalance wallet={_walletMap[sellerId]} className={className} />;
}

export default SellerBalance;
